import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { isForgeAgent } from '../../types.ts';

interface GetDisplayedAgentsProps {
	recommendedAgents: Agent[];
	agentsByCategoryAllAgents: Agent[];
	agentsByCategoryFavoriteAgents: Agent[];
	agentsByCategoryVerifiedAgents?: Agent[];
	selectedAgent?: Agent;
	moveSelectedAgentToTop?: boolean;
	/** Agents to pin at the start of the list, before favorites. */
	defaultAgents?: Agent[];
}

/**
 * Combines default, favourite, verified, recommended and all agents (in that order), with the selected agent at the top (optional)
 * @returns combined relevant agents array
 */
export const getDisplayedAgents = ({
	recommendedAgents,
	agentsByCategoryAllAgents,
	agentsByCategoryFavoriteAgents,
	agentsByCategoryVerifiedAgents = [],
	selectedAgent,
	moveSelectedAgentToTop = true,
	defaultAgents = [],
}: GetDisplayedAgentsProps): Agent[] => {
	const allAgents = [
		...defaultAgents,
		...agentsByCategoryFavoriteAgents,
		...agentsByCategoryVerifiedAgents,
		...recommendedAgents,
		...agentsByCategoryAllAgents,
	];

	const seenAgentIds = new Set();
	const allAgentsDeduplicated = allAgents.filter((agent) => {
		if (seenAgentIds.has(agent.id)) {
			return false;
		}
		seenAgentIds.add(agent.id);
		return true;
	});

	// ensure that the selected agent is at the top of the list
	if (selectedAgent && !selectedAgent.is_default) {
		const index = allAgentsDeduplicated.findIndex((agent) => agent.id === selectedAgent.id);
		if (moveSelectedAgentToTop) {
			if (index !== -1) {
				allAgentsDeduplicated.splice(index, 1);
			}
			allAgentsDeduplicated.unshift(selectedAgent);
		} else if (index === -1) {
			allAgentsDeduplicated.unshift(selectedAgent);
		}
	}

	return allAgentsDeduplicated;
};

export const getAgentIcon = (agent: Agent): React.JSX.Element => {
	return (
		<AgentAvatar
			size="small"
			agentId={agent.id}
			agentNamedId={agent.external_config_reference ?? agent.named_id}
			agentIdentityAccountId={agent.identity_account_id}
			showBorder={false}
			isForgeAgent={
				fg('rovo_agent_support_a2a_avatar')
					? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
					: isForgeAgent(agent)
			}
			forgeAgentIconUrl={agent.icon}
		/>
	);
};
